import { Controller } from '@hotwired/stimulus';
import { subscribeTo } from '../channels/analytics_chat_channel';

export default class extends Controller {
  static targets = ['input'];
  static values = { conversationId: Number };

  connect() {
    if (this.conversationIdValue) {
      subscribeTo(this.conversationIdValue);
    }

    const chatMessages = document.getElementById('chat-messages');
    if (chatMessages) {
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }

    this._statusHandler = (event) => this.handleStatus(event.detail);
    this._messageHandler = (event) => this.handleNewMessage(event.detail);
    document.addEventListener('analytics-chat:status', this._statusHandler);
    document.addEventListener('analytics-chat:message', this._messageHandler);
  }

  disconnect() {
    if (this._statusHandler) {
      document.removeEventListener('analytics-chat:status', this._statusHandler);
    }
    if (this._messageHandler) {
      document.removeEventListener('analytics-chat:message', this._messageHandler);
    }
    this.stopPolling();
  }

  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submitQuestion();
    }
  }

  submitQuestion() {
    const input = this.inputTarget;
    const question = input.value.trim();
    if (!question) return;

    input.value = '';
    input.disabled = true;

    this.appendUserMessage(question);
    this.showAnalyzing(true);

    if (this.conversationIdValue) {
      this.sendFollowUp(question);
    } else {
      this.createConversation(question);
    }
  }

  createConversation(question) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch('/analytics/ask', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ question })
    })
    .then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          try {
            const json = JSON.parse(text);
            throw new Error(json.error || `Server error (${response.status})`);
          } catch (e) {
            if (e.message.includes('Server error') || e.message.includes('Failed')) throw e;
            throw new Error(`Server error (${response.status})`);
          }
        });
      }
      return response.json();
    })
    .then(data => {
      if (data.error) {
        this.showError(data.error);
        return;
      }
      this.conversationIdValue = data.conversation_id;
      subscribeTo(data.conversation_id);

      const emptyState = document.getElementById('empty-state');
      if (emptyState) emptyState.remove();

      // Start polling as fallback in case ActionCable doesn't fire
      this.startPolling();
    })
    .catch(error => {
      this.showError(error.message || 'Failed to start analysis. Please try again.');
      console.error('Analytics create error:', error);
    });
  }

  sendFollowUp(question) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch(`/analytics/conversations/${this.conversationIdValue}/ask`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ question })
    })
    .then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          try {
            const json = JSON.parse(text);
            throw new Error(json.error || `Server error (${response.status})`);
          } catch (e) {
            if (e.message.includes('Server error') || e.message.includes('Failed')) throw e;
            throw new Error(`Server error (${response.status})`);
          }
        });
      }
      return response.json();
    })
    .then(data => {
      if (data?.error) {
        this.showError(data.error);
      } else {
        this.startPolling();
      }
    })
    .catch(error => {
      this.showError(error.message || 'Failed to send question. Please try again.');
      console.error('Analytics ask error:', error);
    });
  }

  // Poll for results as fallback when ActionCable doesn't fire
  startPolling() {
    this.stopPolling();
    this._pollCount = 0;
    this._pollTimer = setInterval(() => {
      this._pollCount++;
      // Poll for up to 5 minutes (every 5 seconds = 60 polls)
      if (this._pollCount > 60) {
        this.stopPolling();
        this.showError('Analysis is taking longer than expected. Refresh the page to check for results.');
        return;
      }
      this.checkForResults();
    }, 5000);
  }

  stopPolling() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  checkForResults() {
    if (!this.conversationIdValue) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(`/analytics/conversations/${this.conversationIdValue}/status`, {
      headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken }
    })
    .then(r => r.json())
    .then(data => {
      if (data.ready) {
        this.stopPolling();
        window.location.href = `/analytics?id=${this.conversationIdValue}`;
      }
    })
    .catch(() => {}); // Silently ignore poll errors
  }

  handleStatus(data) {
    if (data.status === 'done' || data.status === 'error') {
      this.showAnalyzing(false);
      this.stopPolling();
      this.inputTarget.disabled = false;
      this.inputTarget.focus();

      if (data.status === 'done') {
        window.location.href = `/analytics?id=${this.conversationIdValue}`;
      }
    }
  }

  handleNewMessage(_data) {
    this.showAnalyzing(false);
    this.stopPolling();
    this.inputTarget.disabled = false;
    window.location.href = `/analytics?id=${this.conversationIdValue}`;
  }

  useSuggestion(event) {
    event.preventDefault();
    const question = event.currentTarget.dataset.question;
    if (this.hasInputTarget) {
      this.inputTarget.value = question;
      this.submitQuestion();
    }
  }

  expandReport(event) {
    event.preventDefault();
    const modal = document.getElementById('report-fullscreen-modal');
    const iframe = document.getElementById('report-iframe');
    const fullscreenIframe = document.getElementById('report-fullscreen-iframe');
    if (modal && iframe && fullscreenIframe) {
      fullscreenIframe.srcdoc = iframe.srcdoc;
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    }
  }

  closeFullscreen(event) {
    event.preventDefault();
    const modal = document.getElementById('report-fullscreen-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }
  }

  downloadReport(event) {
    event.preventDefault();
    const iframe = document.getElementById('report-iframe');
    if (!iframe) return;

    const html = iframe.srcdoc;
    if (!html) return;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analytics-report.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  toggleSql(event) {
    const toggle = event.currentTarget;
    const targetId = toggle.dataset.targetId;
    const detail = document.getElementById(targetId);
    if (!detail) return;
    const isHidden = detail.style.display === 'none';
    detail.style.display = isHidden ? 'block' : 'none';
    toggle.textContent = isHidden ? 'Hide SQL' : 'SQL';
  }

  appendUserMessage(content) {
    const chatMessages = document.getElementById('chat-messages');
    if (!chatMessages) return;

    const emptyState = document.getElementById('empty-state');
    if (emptyState) emptyState.remove();

    const html = `
      <div class="analytics-item analytics-item-user">
        <div class="analytics-item-header">
          <span class="analytics-role analytics-role-user">You</span>
          <span class="analytics-time">just now</span>
        </div>
        <div class="analytics-item-content"><p>${this.escapeHtml(content)}</p></div>
      </div>
    `;

    const indicator = document.getElementById('analyzing-indicator');
    if (indicator) {
      indicator.insertAdjacentHTML('beforebegin', html);
    } else {
      chatMessages.insertAdjacentHTML('beforeend', html);
    }

    chatMessages.scrollTop = chatMessages.scrollHeight;
  }

  showAnalyzing(show) {
    const indicator = document.getElementById('analyzing-indicator');
    if (indicator) {
      indicator.style.display = show ? '' : 'none';
    }
    const chatMessages = document.getElementById('chat-messages');
    if (chatMessages) {
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }
  }

  showError(message) {
    this.showAnalyzing(false);
    this.stopPolling();
    this.inputTarget.disabled = false;
    this.inputTarget.focus();

    const chatMessages = document.getElementById('chat-messages');
    if (!chatMessages) return;

    const html = `
      <div class="analytics-item analytics-item-assistant">
        <div class="analytics-item-header">
          <span class="analytics-role analytics-role-assistant">AI Analyst</span>
          <span class="analytics-time">just now</span>
        </div>
        <div class="analytics-item-content" style="color: #a94442; font-style: italic;">
          <p>${this.escapeHtml(message)}</p>
        </div>
      </div>
    `;
    chatMessages.insertAdjacentHTML('beforeend', html);
    chatMessages.scrollTop = chatMessages.scrollHeight;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
